import { useMemo } from "react";

import { getPropertyDisplayValue } from "utils/objectData";

export interface PropertyGroup {
    name: string;
    displayName: string;
    properties: [string, string][];
    /**
     * The object's ungrouped base properties. Their search name is the bare property name,
     * without the group prefix. `name` is localized, so it can't be used to detect this.
     */
    isGeneral?: boolean;
}

/** Matches the raw encoded form and the decoded display form (e.g. `%2f` and `/`). */
function matchesSearch(text: string, searchLower: string): boolean {
    return (
        text.toLowerCase().includes(searchLower) || getPropertyDisplayValue(text).toLowerCase().includes(searchLower)
    );
}

export interface UsePropertyFilteringResult {
    filteredGroups: PropertyGroup[];
    hasSearchResults: boolean;
}

export function usePropertyFiltering(
    groupedProperties: PropertyGroup[],
    searchTerm: string = "",
): UsePropertyFilteringResult {
    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) {
            return groupedProperties;
        }

        const searchLower = searchTerm.toLowerCase();
        return groupedProperties
            .map((group) => {
                const matchingProperties = group.properties.filter(
                    ([propName, propValue]) =>
                        matchesSearch(propName, searchLower) || matchesSearch(propValue, searchLower),
                );

                return matchingProperties.length > 0 ? { ...group, properties: matchingProperties } : null;
            })
            .filter((group): group is PropertyGroup => group !== null);
    }, [groupedProperties, searchTerm]);

    const hasSearchResults = filteredGroups.length > 0;

    return {
        filteredGroups,
        hasSearchResults,
    };
}
